<template>
  <modal
    :open="open"
    title="Set matches found"
    size="full"
    @close="handleClose"
  >
    <template v-if="open && setMatchesList.length > 0">
      <h4 v-if="profileInfo">
        {{ profileSummary || '—' }}
      </h4>
      <p class="set-matches-intro">
        The following profiles in this set can be merged using the same mapping. Select which to merge and confirm.
      </p>

      <div class="set-matches-list">
        <div class="set-match-row set-match-row-select-all">
          <checkbox
            :value="allSetMatchesSelected"
            :label="allSetMatchesSelected ? 'Deselect all' : 'Select all'"
            class="set-match-checkbox"
            @input="toggleSelectAll"
          />
        </div>
        <profile-set-match-row
          v-for="(item, i) in setMatchesList"
          :key="`${item.numberSlug}-${i}`"
          :item="item"
          :selected="selectedIndices[i] === true"
          @toggle="setSelected(i, $event)"
        />
      </div>
    </template>

    <template v-if="open" #footer>
      <align justify="space-between">
        <btn
          kind="secondary"
          @click="handleClose"
        >
          Cancel
        </btn>
        <btn
          :loading="confirming"
          :disabled="!selectionStats.hasSelected"
          @click="confirmMerge"
        >
          Confirm merge {{ selectionStats.selectedCount }} selected
        </btn>
      </align>
    </template>
  </modal>
</template>

<script>
import * as Sentry from '@sentry/vue';
import firebase from 'firebase/compat/app';

import ProfileSetMatchRow from './ProfileSetMatchRow.vue';

export default {
  components: {
    ProfileSetMatchRow,
  },

  props: {
    open: {
      type: Boolean,
      default: false,
    },
    suggestedSetMatches: {
      type: Array,
      default: () => [],
    },
    profileInfo: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      selectedIndices: [],
      confirming: false,
    };
  },

  watch: {
    open(open) {
      if (open) {
        const matches = this.setMatchesList;
        if (matches.length > 0) {
          this.selectedIndices = matches.map(() => true);
        } else {
          this.selectedIndices = [];
        }
      } else {
        this.selectedIndices = [];
      }
    },
  },

  computed: {
    setMatchesList() {
      return this.suggestedSetMatches && Array.isArray(this.suggestedSetMatches) ? this.suggestedSetMatches : [];
    },

    /** One pass over selection — avoids three full scans (some/filter/every) on every toggle. */
    selectionStats() {
      const n = this.setMatchesList.length;
      const sel = this.selectedIndices;
      let selectedCount = 0;
      for (let i = 0; i < n; i++) {
        if (sel[i]) selectedCount++;
      }
      return {
        selectedCount,
        hasSelected: selectedCount > 0,
        allSelected: n > 0 && selectedCount === n && sel.length === n,
      };
    },

    allSetMatchesSelected() {
      return this.selectionStats.allSelected;
    },

    profileSummary() {
      const p = this.profileInfo;
      if (!p) return '';
      const parts = [];
      if (p.category != null && p.category !== '') parts.push(p.category);
      if (p.year != null && p.year !== '') parts.push(p.year);
      if (p.set != null && p.set !== '') parts.push(p.set);
      if (p.variation != null && p.variation !== '') parts.push(p.variation);
      return parts.join(' ') || '';
    },
  },

  methods: {
    setSelected(index, selected) {
      this.$set(this.selectedIndices, index, selected);
    },

    toggleSelectAll() {
      const list = this.setMatchesList;
      if (this.allSetMatchesSelected) {
        this.selectedIndices = list.map(() => false);
      } else {
        this.selectedIndices = list.map(() => true);
      }
    },

    handleClose() {
      this.$emit('close');
    },

    async confirmMerge() {
      if (!this.selectionStats.hasSelected || this.confirming) return;

      const toMerge = [];
      this.setMatchesList.forEach((item, i) => {
        if (this.selectedIndices[i] && item.profiles?.length) {
          toMerge.push({
            profiles: item.profiles.map((p) => ({ profileId: p.profileId, grader: p.grader })),
          });
        }
      });
      if (toMerge.length === 0) return;

      try {
        this.confirming = true;
        const employeeId = this.$store?.state?.user?.uid || null;
        const matchCallable = firebase.functions().httpsCallable('httpmatchprofiles');
        const results = await Promise.all(toMerge.map((g) => matchCallable({
          profiles: g.profiles,
          employeeId,
          reportType: 'PROFILE_MATCHED_AUTOMATIC',
        })));
        const successCount = results.filter((r) => r?.data?.ok).length;

        this.$toasted.success(`Merged ${successCount} group(s)`);
        this.handleClose();
      } catch (e) {
        this.$toasted.error(e.message || 'Failed to merge some profiles');
        Sentry.captureException(e);
      } finally {
        this.confirming = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.set-matches-intro {
  margin-bottom: $gp;
}

.set-matches-list {
  display: flex;
  flex-direction: column;
  gap: $gp * 0.5;
}

.set-match-row-select-all {
  padding-bottom: $gp * 0.5;
  border-bottom: 1px solid var(--theme-border);
  margin-bottom: $gp * 0.25;
}

.set-match-row {
  display: flex;
  align-items: flex-start;
  gap: $gp;
}

.set-match-checkbox {
  flex-shrink: 0;
  --checkbox-margin-bottom: 0;
}
</style>
